// apps/admin/src/components/monitoring/UploadTraceViewer.tsx

import React, { useEffect, useState } from 'react';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  collectionGroup,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import {
  X,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Upload,
  FileText,
  Database,
  BarChart3,
  Circle,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface TraceStage {
  name: string;
  label: string;
  icon: React.ReactNode;
  status: 'completed' | 'failed' | 'in_progress' | 'pending';
  timestamp?: string;
  duration?: number;
  details?: string;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
}

interface UploadTraceViewerProps {
  uploadId: string;
  onClose: () => void;
}

// ============================================
// HELPERS
// ============================================

function formatTimestamp(ts: any): string {
  if (!ts) return 'N/A';

  // Handle Firestore Timestamp
  if (ts.toDate) {
    return ts.toDate().toLocaleString('en-AU', { timeZone: 'Australia/Sydney' });
  }
  // Handle string timestamps
  if (typeof ts === 'string') {
    return new Date(ts).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' });
  }
  // Handle Date objects
  if (ts instanceof Date) {
    return ts.toLocaleString('en-AU', { timeZone: 'Australia/Sydney' });
  }
  return String(ts);
}

function formatDuration(ms: number): string {
  if (!ms || ms <= 0) return 'N/A';
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

// ============================================
// COMPONENT
// ============================================

export const UploadTraceViewer: React.FC<UploadTraceViewerProps> = ({
  uploadId,
  onClose,
}) => {
  const [stages, setStages] = useState<TraceStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadMeta, setUploadMeta] = useState<Record<string, any>>({});

  useEffect(() => {
    fetchTrace();
  }, [uploadId]);

  const fetchTrace = async () => {
    try {
      setLoading(true);
      setError(null);

      const traceStages: TraceStage[] = [];
      const meta: Record<string, any> = {};

      // ============================================
      // STAGE 0: Ingress - Check upload_intents
      // ============================================
      try {
        const intentRef = doc(db, 'upload_intents', uploadId);
        const intentSnap = await getDoc(intentRef);

        if (intentSnap.exists()) {
          const intentData = intentSnap.data();
          meta.companyId = intentData.companyId;
          meta.templateId = intentData.templateId;
          meta.officeId = intentData.officeId;
          meta.fileName = intentData.fileName || intentData.sanitizedFileName;

          traceStages.push({
            name: 'ingress',
            label: 'Stage 0: Ingress',
            icon: <Upload className="w-5 h-5" />,
            status: 'completed',
            timestamp: formatTimestamp(intentData.createdAt || intentData.timestamp),
            details: `Signed URL issued for ${intentData.fileName || 'file'}`,
            metadata: {
              companyId: intentData.companyId,
              templateId: intentData.templateId,
              officeId: intentData.officeId,
              storagePath: intentData.storagePath,
            },
          });
        } else {
          traceStages.push({
            name: 'ingress',
            label: 'Stage 0: Ingress',
            icon: <Upload className="w-5 h-5" />,
            status: 'pending',
            details: 'Upload intent not found - may have been cleaned up',
          });
        }
      } catch (err) {
        traceStages.push({
          name: 'ingress',
          label: 'Stage 0: Ingress',
          icon: <Upload className="w-5 h-5" />,
          status: 'pending',
          details: 'Could not query upload_intents',
        });
      }

      // ============================================
      // STAGE 1+2: Staging + Processing - Check client_processing_queue
      // ============================================
      try {
        // Try direct document lookup first (uploadId may be the queue doc ID)
        const queueRef = doc(db, 'client_processing_queue', uploadId);
        const queueSnap = await getDoc(queueRef);

        let queueData: any = null;

        if (queueSnap.exists()) {
          queueData = queueSnap.data();
        } else {
          // Try querying by uploadId field
          const queueQuery = query(
            collection(db, 'client_processing_queue'),
            where('uploadId', '==', uploadId)
          );
          const queueResults = await getDocs(queueQuery);

          if (!queueResults.empty) {
            queueData = queueResults.docs[0].data();
          }
        }

        if (queueData) {
          meta.processingStatus = queueData.status;
          meta.recordCount = queueData.recordCount;

          // Stage 1: Staging
          traceStages.push({
            name: 'staging',
            label: 'Stage 1: Staging',
            icon: <FileText className="w-5 h-5" />,
            status: queueData.status === 'failed' ? 'failed' : 'completed',
            timestamp: formatTimestamp(queueData.queuedAt || queueData.createdAt),
            details: `File queued for processing (${queueData.periodMonth || 'unknown period'}, Run ${queueData.runNumber || '?'})`,
            metadata: {
              periodMonth: queueData.periodMonth,
              runNumber: queueData.runNumber,
              fileName: queueData.fileName,
            },
          });

          // Stage 2: Processing
          const processingStatus =
            queueData.status === 'complete' || queueData.status === 'completed'
              ? 'completed'
              : queueData.status === 'failed'
                ? 'failed'
                : queueData.status === 'processing'
                  ? 'in_progress'
                  : 'pending';

          traceStages.push({
            name: 'processing',
            label: 'Stage 2: Processing',
            icon: <Database className="w-5 h-5" />,
            status: processingStatus as TraceStage['status'],
            timestamp: formatTimestamp(queueData.processedAt || queueData.completedAt),
            duration: queueData.durationMs,
            details:
              processingStatus === 'completed'
                ? `BigQuery write complete (${queueData.recordCount || 0} records)`
                : processingStatus === 'failed'
                  ? 'Processing failed'
                  : processingStatus === 'in_progress'
                    ? 'Currently processing...'
                    : 'Awaiting processing',
            errorMessage: queueData.errorMessage || queueData.error,
            metadata: {
              recordCount: queueData.recordCount,
              durationMs: queueData.durationMs,
              status: queueData.status,
            },
          });
        } else {
          traceStages.push({
            name: 'staging',
            label: 'Stage 1: Staging',
            icon: <FileText className="w-5 h-5" />,
            status: 'pending',
            details: 'Queue document not found',
          });

          traceStages.push({
            name: 'processing',
            label: 'Stage 2: Processing',
            icon: <Database className="w-5 h-5" />,
            status: 'pending',
            details: 'Awaiting staging',
          });
        }
      } catch (err) {
        traceStages.push({
          name: 'staging',
          label: 'Stage 1: Staging',
          icon: <FileText className="w-5 h-5" />,
          status: 'pending',
          details: 'Could not query processing queue',
        });

        traceStages.push({
          name: 'processing',
          label: 'Stage 2: Processing',
          icon: <Database className="w-5 h-5" />,
          status: 'pending',
          details: 'Could not query processing queue',
        });
      }

      // ============================================
      // STAGE 3: Aggregation - Check aggregation_triggers
      // ============================================
      try {
        // aggregation_triggers is under companies/{companyId}/aggregation_triggers
        // We need the companyId from previous stages
        if (meta.companyId) {
          const aggQuery = query(
            collection(db, `companies/${meta.companyId}/aggregation_triggers`),
            where('uploadId', '==', uploadId)
          );
          const aggResults = await getDocs(aggQuery);

          if (!aggResults.empty) {
            const aggData = aggResults.docs[0].data();

            const aggStatus =
              aggData.status === 'complete' || aggData.status === 'completed'
                ? 'completed'
                : aggData.status === 'failed'
                  ? 'failed'
                  : aggData.status === 'processing'
                    ? 'in_progress'
                    : 'pending';

            traceStages.push({
              name: 'aggregation',
              label: 'Stage 3: Aggregation',
              icon: <BarChart3 className="w-5 h-5" />,
              status: aggStatus as TraceStage['status'],
              timestamp: formatTimestamp(aggData.triggeredAt || aggData.createdAt),
              duration: aggData.durationMs,
              details:
                aggStatus === 'completed'
                  ? 'Aggregation complete'
                  : aggStatus === 'failed'
                    ? 'Aggregation failed'
                    : aggStatus === 'in_progress'
                      ? 'Aggregation in progress...'
                      : 'Awaiting aggregation trigger',
              errorMessage: aggData.errorMessage || aggData.error,
              metadata: {
                status: aggData.status,
                durationMs: aggData.durationMs,
              },
            });
          } else {
            traceStages.push({
              name: 'aggregation',
              label: 'Stage 3: Aggregation',
              icon: <BarChart3 className="w-5 h-5" />,
              status: 'pending',
              details: 'No aggregation trigger found for this upload',
            });
          }
        } else {
          traceStages.push({
            name: 'aggregation',
            label: 'Stage 3: Aggregation',
            icon: <BarChart3 className="w-5 h-5" />,
            status: 'pending',
            details: 'Company ID unknown - cannot check aggregation',
          });
        }
      } catch (err) {
        traceStages.push({
          name: 'aggregation',
          label: 'Stage 3: Aggregation',
          icon: <BarChart3 className="w-5 h-5" />,
          status: 'pending',
          details: 'Could not query aggregation triggers',
        });
      }

      setStages(traceStages);
      setUploadMeta(meta);
    } catch (err: any) {
      console.error('Failed to fetch upload trace:', err);
      setError(err.message || 'Failed to load trace data');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // STATUS ICON
  // ============================================

  const getStatusIcon = (status: TraceStage['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-emerald-400" />;
      case 'failed':
        return <XCircle className="w-6 h-6 text-red-400" />;
      case 'in_progress':
        return <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />;
      case 'pending':
        return <Circle className="w-6 h-6 text-slate-500" />;
    }
  };

  const getStatusColor = (status: TraceStage['status']) => {
    switch (status) {
      case 'completed':
        return 'border-emerald-500/50';
      case 'failed':
        return 'border-red-500/50';
      case 'in_progress':
        return 'border-blue-500/50';
      case 'pending':
        return 'border-slate-600/50';
    }
  };

  const getConnectorColor = (status: TraceStage['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-500/50';
      case 'failed':
        return 'bg-red-500/50';
      case 'in_progress':
        return 'bg-blue-500/50';
      case 'pending':
        return 'bg-slate-600/50';
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-400" />
              Upload Trace
            </h2>
            <p className="text-sm text-slate-400 mt-1 font-mono">
              {uploadId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-700 transition-colors text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-3" />
                <p className="text-slate-400">Loading trace data...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <p className="text-red-300 font-medium">Failed to load trace</p>
              </div>
              <p className="text-sm text-red-300">{error}</p>
            </div>
          ) : (
            <>
              {/* Upload Metadata */}
              {Object.keys(uploadMeta).length > 0 && (
                <div className="mb-6 bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Upload Details</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {uploadMeta.companyId && (
                      <>
                        <span className="text-slate-500">Company:</span>
                        <span className="text-white font-mono text-xs">
                          {uploadMeta.companyId}
                        </span>
                      </>
                    )}
                    {uploadMeta.templateId && (
                      <>
                        <span className="text-slate-500">Template:</span>
                        <span className="text-white font-mono text-xs">
                          {uploadMeta.templateId}
                        </span>
                      </>
                    )}
                    {uploadMeta.officeId && (
                      <>
                        <span className="text-slate-500">Office:</span>
                        <span className="text-white font-mono text-xs">
                          {uploadMeta.officeId}
                        </span>
                      </>
                    )}
                    {uploadMeta.fileName && (
                      <>
                        <span className="text-slate-500">File:</span>
                        <span className="text-white font-mono text-xs">
                          {uploadMeta.fileName}
                        </span>
                      </>
                    )}
                    {uploadMeta.processingStatus && (
                      <>
                        <span className="text-slate-500">Status:</span>
                        <span className="text-white font-mono text-xs uppercase">
                          {uploadMeta.processingStatus}
                        </span>
                      </>
                    )}
                    {uploadMeta.recordCount !== undefined && (
                      <>
                        <span className="text-slate-500">Records:</span>
                        <span className="text-white font-mono text-xs">
                          {uploadMeta.recordCount}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Vertical Waterfall Timeline */}
              <div className="relative">
                {stages.map((stage, idx) => (
                  <div key={stage.name} className="relative flex gap-4">
                    {/* Vertical connector line */}
                    <div className="flex flex-col items-center">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-full border-2 ${getStatusColor(stage.status)} bg-slate-800 flex items-center justify-center`}>
                        {getStatusIcon(stage.status)}
                      </div>
                      {idx < stages.length - 1 && (
                        <div className={`w-0.5 flex-1 min-h-[24px] ${getConnectorColor(stages[idx + 1].status)}`} />
                      )}
                    </div>

                    {/* Stage content */}
                    <div className={`flex-1 pb-6 ${idx === stages.length - 1 ? 'pb-0' : ''}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium">{stage.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          stage.status === 'completed'
                            ? 'bg-emerald-500/20 text-emerald-300'
                            : stage.status === 'failed'
                              ? 'bg-red-500/20 text-red-300'
                              : stage.status === 'in_progress'
                                ? 'bg-blue-500/20 text-blue-300'
                                : 'bg-slate-600/20 text-slate-400'
                        }`}>
                          {stage.status === 'completed' ? 'Complete' :
                           stage.status === 'failed' ? 'Failed' :
                           stage.status === 'in_progress' ? 'In Progress' :
                           'Pending'}
                        </span>
                      </div>

                      <p className="text-sm text-slate-400">{stage.details}</p>

                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                        {stage.timestamp && stage.timestamp !== 'N/A' && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {stage.timestamp}
                          </span>
                        )}
                        {stage.duration && stage.duration > 0 && (
                          <span>Duration: {formatDuration(stage.duration)}</span>
                        )}
                      </div>

                      {/* Error message for failed stages */}
                      {stage.status === 'failed' && stage.errorMessage && (
                        <div className="mt-2 bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                          <p className="text-xs text-red-300 font-mono break-words">
                            {stage.errorMessage}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Empty state */}
              {stages.length === 0 && (
                <div className="text-center py-8">
                  <Circle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No trace data found for this upload</p>
                  <p className="text-sm text-slate-500 mt-1">
                    The upload may have been cleaned up or the ID may be incorrect
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/50">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Pipeline: Ingress &rarr; Staging &rarr; Processing &rarr; Aggregation
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 text-white text-sm rounded-lg hover:bg-slate-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
